import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { db } from "../../firebase/firebaseConfig";
import { doc, getDoc, getDocs, updateDoc, collection, query, onSnapshot, orderBy, setDoc, getDoc as getDocOnce, deleteDoc,} from "firebase/firestore";
import { serverTimestamp } from "firebase/firestore";
import Button from "../../components/Button";
import { useClaims } from "../../providers/ClaimsProvider";

import * as XLSX from "xlsx-js-style";

const computeIMC = (peso, talla) => {
  const w = Number(peso);
  const h = Number(talla) / 100;
  if (!w || !h) return null;
  const imc = w / (h * h);
  return isFinite(imc) ? Number(imc.toFixed(2)) : null;
};

// ===== Definiciones =====
const CUESTIONARIOS_DEF = [
  { id: "EAT-10", label: "Cribado disfagia (EAT-10)" },
  { id: "CRIBADO-NUTRICIONAL", label: "Cribado nutricional" },
  { id: "CRITERIOS-GLIM", label: "Valoración nutricional" },
  { id: "CAVIDAD-ORAL", label: "Valoración cavidad oral" },
  { id: "MECV-V", label: "MECV-V" },
];
const ORDER_INDEX = Object.fromEntries(CUESTIONARIOS_DEF.map((q, i) => [q.id, i]));
const LABEL_BY_ID = Object.fromEntries(CUESTIONARIOS_DEF.map((q) => [q.id, q.label]));

const RECURSOS_DEF = [
  { path: "/recomendaciones", label: "Recomendaciones" },
  { path: "/recetas", label: "Recetas" },
  { path: "/ejercicios", label: "Actividad / Ejercicio físico" },
  { path: "/disfagia-y-farmacos", label: "Disfagia y fármacos" },
  { path: "/pautas-ejercicios-logopedia-rehabilitacion", label: "Pautas / Ejercicios de logopedia y rehabilitación" }
];

const clasificarCribadoNutricional = (tipo, puntuacion) => {
  if (tipo === "MNA") {
    if (puntuacion >= 12) return "Estado nutricional normal";
    if (puntuacion >= 8) return "Riesgo de malnutrición";
    return "Malnutrición";
  }

  if (tipo === "MST") {
    return puntuacion >= 2
      ? "Riesgo de malnutrición"
      : "Sin riesgo de malnutrición";
  }

  if (tipo === "NRS") {
    return puntuacion >= 3
      ? "Riesgo nutricional"
      : "Sin riesgo nutricional";
  }

  if (tipo === "MUST") {
    if (puntuacion === 0) return "Riesgo bajo";
    if (puntuacion === 1) return "Riesgo medio";
    return "Riesgo alto";
  }

  return "";
};

export default function VisitaDetalle() {
  const { id: pacienteId, vid } = useParams();
  const navigate = useNavigate();
  const { user, role, loading } = useClaims();

  const [visita, setVisita] = useState(null);
  const [cuestionarios, setCuestionarios] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [eliminando, setEliminando] = useState(false);
const numeroVisita = visita?.numeroVisita ?? null;

  const [pautas, setPautas] = useState(null);

  const visitaRef = useMemo(() => doc(db, "pacientes", pacienteId, "visitas", vid), [pacienteId, vid]);
  const respRef = useMemo(() => collection(db, "pacientes", pacienteId, "visitas", vid, "respuestas"), [pacienteId, vid]);

  const esElevado = role === "investigador_principal" || role === "jefe" || role === "investigador";

  useEffect(() => {
    (async () => {
      const snap = await getDoc(visitaRef);
      if (!snap.exists()) return;
      const raw = { id: snap.id, ...snap.data() };

      if (!raw.creadoPor && user) {
        try {
          await updateDoc(visitaRef, {
            creadoPor: user.uid,
            creadoPorEmail: user.email || null,
          });
          raw.creadoPor = user.uid;
          raw.creadoPorEmail = user.email || null;
        } catch (e) {
          console.warn("Backfill creadoPor falló:", e.code || e.message);
        }
      }

      const peso = raw.medidas?.peso ?? raw.peso ?? null;
      const talla = raw.medidas?.talla ?? raw.talla ?? null;
      const imcCalc = raw.medidas?.imc ?? raw.imc ?? (peso != null && talla != null ? computeIMC(peso, talla) : null);

      setVisita({
        ...raw,
        medidas: { ...(raw.medidas || {}), peso, talla, imc: imcCalc },
      });
    })();
  }, [visitaRef, user]);

  useEffect(() => {
    const q = query(respRef, orderBy("instrumentoId"));
    const unsub = onSnapshot(q, async (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setCuestionarios(rows);

      const hay = rows.length > 0;
      const todosOk = rows.every((r) => r.completado === true);
      const completada = hay && todosOk;

      try {
        const vs = await getDoc(visitaRef);
        if (vs.exists() && vs.data()?.completada !== completada) {
          await updateDoc(visitaRef, {
            completada,
            estado: completada ? "Completo" : "Pendiente",
          });
          setVisita((prev) => ({
            ...(prev || {}),
            completada,
            estado: completada ? "Completo" : "Pendiente",
          }));
        }
      } catch (e) {
        console.warn("No se pudo actualizar 'completada':", e.code || e.message);
      }
    });
    return () => unsub();
  }, [respRef, visitaRef]);

// ===== NUEVO: leer pautas/main (solo lectura) =====
useEffect(() => {
  const fetchPautas = async () => {
    try {
      const ref = doc(
        db,
        "pacientes",
        pacienteId,
        "visitas",
        vid,
        "pautas",
        "main"
      );
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setPautas(snap.data());
      } else {
        setPautas(null);
      }
    } catch (e) {
      console.warn("No se pudieron cargar las pautas:", e.message);
    }
  };

  if (pacienteId && vid) fetchPautas();
}, [pacienteId, vid]);

  const onChangeMedida = (campo, valor) => {
    setVisita((prev) => {
      const medidas = { ...(prev?.medidas || {}), [campo]: valor };
      const imc = computeIMC(medidas.peso, medidas.talla);
      return { ...(prev || {}), medidas: { ...medidas, imc } };
    });
  };

  const puedeEditarVisita = useMemo(() => {
    if (!user || !visita) return false;
    if (esElevado) return true;
    return visita.creadoPor === user.uid;
  }, [user, visita, esElevado]);

  const sortedCuestionarios = useMemo(() => {
    return [...cuestionarios].sort((a, b) => {
      const ai = ORDER_INDEX[a.instrumentoId] ?? 999;
      const bi = ORDER_INDEX[b.instrumentoId] ?? 999;
      if (ai !== bi) return ai - bi;
      return String(a.instrumentoId).localeCompare(String(b.instrumentoId));
    });
  }, [cuestionarios]);

  // === Ayuda: nombre legible del cuestionario ===
  const nombreCuestionario = (c) =>
    LABEL_BY_ID[c?.instrumentoId] ||
    LABEL_BY_ID[c?.id] ||
    c?.instrumentoId ||
    c?.id ||
    "Cuestionario";

  const fechaFormateada = visita?.fecha
    ? new Date(visita.fecha)
        .toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" })
        .replace(/\//g, "-")
    : "—";

const safeSheetName = (name) => {
  const trimmed = String(name || "Hoja")
    .replace(/[*?:\\[\]]/g, " ")
    .slice(0, 31)
    .trim();
  return trimmed || "Hoja";
};

const descargarExcel = async () => {
  try {
    const wb = XLSX.utils.book_new();

    // ======================
    // HOJA 1: VISITA
    // ======================
    const wsVisita = XLSX.utils.json_to_sheet([
      {
        "Código": pacienteId,
        "Número de visita": numeroVisita ?? "",
        "Fecha de la visita": fechaFormateada,
        "Estado de la visita": visita.estado || (visita.completada ? "Completo" : "Pendiente"),
        "Peso (kg)": visita.medidas?.peso ?? "",
        "Talla (cm)": visita.medidas?.talla ?? "",
        "IMC": visita.medidas?.imc ?? "",
        "Creado por...": visita.creadoPorEmail || "—",
      },
    ]);
    XLSX.utils.book_append_sheet(wb, wsVisita, "Visita");

wsVisita["!cols"] = [
  { wch: 25 },
  { wch: 20 },
  { wch: 18 },
  { wch: 18 },
  { wch: 16 },
  { wch: 16 },
  { wch: 14 },
  { wch: 45 },
];

{
  const range = XLSX.utils.decode_range(wsVisita["!ref"]);
  for (let R = range.s.r; R <= range.e.r; ++R) {
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const addr = XLSX.utils.encode_cell({ r: R, c: C });
      const cell = wsVisita[addr];
      if (!cell) continue;

      cell.s = {
        alignment: {
          horizontal: "center",
          vertical: "center",
          wrapText: true,
        },
        font: {
          bold: R === 0,
          sz: 12,
        },
      };
    }
  }
}

// ======================
// CUESTIONARIOS (1 HOJA POR CUESTIONARIO)
// ======================

const ORDEN_CUESTIONARIOS = [
  "EAT-10",
  "CRIBADO-NUTRICIONAL",
  "CRITERIOS-GLIM",
  "CAVIDAD-ORAL",
  "MECV-V",
];

const LABEL_CUESTIONARIOS = {
  "EAT-10": "Cribado disfagia",
  "CRIBADO-NUTRICIONAL": "Cribado nutricional",
  "CRITERIOS-GLIM": "Val. nutricional",
  "CAVIDAD-ORAL": "Val. cavidad oral",
  "MECV-V": "MECV-V",
};

// ---------------------
// EAT-10
// ---------------------
const EAT10_PREGUNTAS = {
  "0": "1. Mi problema para tragar me ha llevado a perder peso",
  "1": "2. Mi problema para tragar interfiere con mi capacidad para comer fuera de casa",
  "2": "3. Tragar líquidos me supone un esfuerzo extra",
  "3": "4. Tragar sólidos me supone un esfuerzo extra",
  "4": "5. Tragar pastillas me supone un esfuerzo extra",
  "5": "6. Tragar es doloroso",
  "6": "7. El placer de comer se ve afectado por mi problema para tragar",
  "7": "8. Cuando trago, la comida se pega en mi garganta",
  "8": "9. Toso cuando como",
  "9": "10. Tragar es estresante",
};

const EAT10_RESPUESTAS = {
  "0": "0 - Ningún problema",
  "4": "4 - Problema serio",
};

// ---------------------
// CRIBADO NUTRICIONAL
// ---------------------

const TIPO_PACIENTE_LABEL = {
  "Paciente mayor de 65 años": "Paciente mayor de 65 años (MNA)",
  "Paciente con cáncer": "Paciente con cáncer (MST)",
  "Paciente hospitalizado": "Paciente hospitalizado (NRS)",
  "Paciente ambulatorio": "Paciente ambulatorio (MUST)",
};

const MNA_PREGUNTAS = {
  A: "A. ¿Ha perdido el apetito?... últimos 3 meses?",
  B: "B. Pérdida reciente de peso (< 3 meses)",
  C: "C. Movilidad",
  D: "D. ¿Enfermedad aguda o estrés psicológico en los últimos 3 meses?",
  E: "E. Problemas neuropsicológicos",
  F: "F. Índice de masa corporal (IMC)",
  G: "G. ¿El paciente vive independiente en su domicilio?",
  H: "H. ¿Toma más de 3 medicamentos al día?",
  I: "I. ¿Úlceras o lesiones cutáneas?",
  J: "J. ¿Cuántas comidas completas toma al día?",
  K: "K. ¿Consume el paciente…",
  K1: "…productos lácteos al menos una vez al día?",
  K2: "…huevos o legumbres 1 o 2 veces a la semana?",
  K3: "…carne, pescado o aves, diariamente?",
  L: "L. ¿Consume frutas o verduras al menos 2 veces al día?",
  M: "M. ¿Cuántos vasos de agua u otros líquidos toma al día?",
  N: "N. Forma de alimentarse",
  O: "O. ¿Se considera el paciente que está bien nutrido?",
  P: "P. En comparación con las personas de su edad, ¿cómo encuentra su estado de salud?",
  Q: "Q. Circunferencia braquial (CB en cm)",
  R: "R. Circunferencia de la pantorrilla (CP en cm)",
};

const MNA_RESPUESTAS = {
  A: {
    "0": "0 - ha comido mucho menos",
    "1": "1 - ha comido menos",
    "2": "2 - ha comido igual",
  },
  B: {
    "0": "0 - > 3 kg",
    "1": "1 - no lo sabe",
    "2": "2 - pérdida entre 1 a 3 kg",
    "3": "3 - no ha habido peso",
  },
  C: {
    "0": "0 - de la cama al sillón",
    "1": "1 - autonomía en el interior",
    "2": "2 - sale del domicilio",
  },
  D: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  E: {
    "0": "0 - demencia o depresión grave",
    "1": "1 - demencia leve",
    "2": "2 - sin problemas psicológicos",
  },
  F: {
    "0": "0 - IMC < 19",
    "1": "1 - 19 ≤ IMC < 21",
    "2": "2 - 21 ≤ IMC < 23",
    "3": "3 - IMC ≥ 23",
  },
  G: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  H: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  I: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  J: {
    "0": "0 - 1 comida",
    "1": "1 - 2 comidas",
    "2": "2 - 3 comidas",
  },
  K1: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  K2: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  K3: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  L: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  M: {
    "0": "0 - menos de 3 vasos",
    "0.5": "0.5 - de 3 a 5 vasos",
    "1": "1 - más de 5 vasos",
  },
  N: {
    "0": "0 - necesita ayuda",
    "1": "1 - se alimenta solo con dificultad",
    "2": "2 - se alimenta solo sin dificultad",
  },
  O: {
    "0": "0 - malnutrición grave",
    "1": "1 - no lo sabe o malnutrición moderada",
    "2": "2 - sin problemas de nutrición",
  },
  P: {
    "0": "0 - peor",
    "0.5": "0.5 - no lo sabe",
    "1": "1 - igual",
    "2": "2 - mejor",
  },
  Q: {
    "0": "0 - CB < 21",
    "0.5": "0.5 - 21 ≤ CB ≤ 22",
    "1": "1 - CB > 22",
  },
  R: {
    "0": "0 - CP < 31",
    "1": "1 - CP ≥ 31",
  },
};

const MST_PREGUNTAS = {
  q1: "1. ¿Ha perdido peso de manera involuntaria recientemente?",
  q1b: "Si ha perdido peso, ¿cuántos kilogramos?",
  q2: "2. ¿Ha comido menos de lo normal a causa de la disminución del apetito?",
};

const MST_RESPUESTAS = {
  q1: {
    "0": "0 - no",
    "2": "2 - no estoy seguro",
  },
  q1b: {
    "1": "1 - de 1 a 5 kg",
    "2": "2 - de 6 a 10 kg",
    "3": "3 - de 11 a 15 kg",
    "4": "4 - > 15 kg",
    "0": "0 - no estoy seguro",
  },
  q2: {
    "0": "0 - no",
    "1": "1 - sí",
  },
};

const NRS_PREGUNTAS = {
  n1: "1. ¿IMC < 20.5?",
  n2: "2. ¿Pérdida de peso en los últimos 3 meses?",
  n3: "3. ¿Disminución de la ingesta en la última semana?",
  n4: "4. ¿Enfermedad grave?",
};

const NRS_RESPUESTAS = {
  n1: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  n2: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  n3: {
    "0": "0 - no",
    "1": "1 - sí",
  },
  n4: {
    "0": "0 - no",
    "1": "1 - sí",
  },
};

const MUST_PREGUNTAS = {
  step1: "Paso 1. IMC = peso en kg / talla en m²",
  step2: "Paso 2. Pérdida de peso involuntaria en los últimos 3-6 meses",
  step3: "Paso 3. Paciente con enfermedad aguda y sin aporte > 5 días",
};

const MUST_RESPUESTAS = {
  step1: {
    "0": "0 - ≥ 20",
    "1": "1 - 18.5 a 20",
    "2": "2 - ≤ 18.5",
  },
  step2: {
    "0": "0 - ≤ 5 %",
    "1": "1 - 5 a 10 %",
    "2": "2 - ≥ 10 %",
  },
  step3: {
    "0": "0 - no",
    "1": "1 - sí",
  },
};

// ---------------------
// GLIM – Preguntas
// ---------------------
const GLIM_PREGUNTAS = {
  p1: "CRITERIOS FENOTÍPICOS - 1. Pérdida de peso",
  p2: "CRITERIOS FENOTÍPICOS - 2. Índice de Masa Corporal (IMC)",
  p3: "CRITERIOS FENOTÍPICOS - 3. Masa muscular reducida",
  p4: "CRITERIOS ETIOLÓGICOS - 4. Ingesta o asimilación reducida",
  p5: "CRITERIOS ETIOLÓGICOS - 5. Enfermedad o inflamación",
};

// ---------------------
// CAVIDAD-ORAL – Preguntas
// ---------------------
const CAVIDAD_ORAL_PREGUNTAS = {
  q1: "1. Piezas dentarias",
  q2: "2. Dentadura postiza",
  q3: "3. Mucositis",
  q4: "4. Lengua y encías",
  q5: "5. Salivación",
  q6: "6. Halitosis (mal aliento)",
  q7: "7. Dolor o molestias orales",
};

// Agrupar respuestas por instrumento
const respuestasPorInstrumento = {};

sortedCuestionarios.forEach((q) => {
  const id = q.instrumentoId || q.id;
  respuestasPorInstrumento[id] = q;
});

// Crear hojas por cuestionario
for (const instrumentoId of ORDEN_CUESTIONARIOS) {
  const q = respuestasPorInstrumento[instrumentoId];
  if (!q) continue;

  const nombreHoja = safeSheetName(
    `Visita ${numeroVisita} – ${LABEL_CUESTIONARIOS[instrumentoId]}`
  );

  const estadoTexto = q.completado ? "Completo" : "Pendiente";
  const rows = [];

  // =========================
  // EAT-10 (FORMATO ESPECIAL)
  // =========================
  if (instrumentoId === "EAT-10") {

    const puntuacionTotal = Object.values(q.items || {}).reduce(
      (acc, v) => acc + Number(v || 0),
      0
    );

    rows.push({
      "Nombre del cuestionario": "Cribado disfagia (EAT-10)",
      "Estado": estadoTexto,
      "Preguntas": "",
      "Respuestas": "",
      "Puntuación total": "",
      "Resultado": "",
    });

    Object.entries(q.items || {}).forEach(([k, v]) => {
      rows.push({
        "Nombre del cuestionario": "",
        "Estado": "",
        "Preguntas": EAT10_PREGUNTAS[k] || k,
        "Respuestas": EAT10_RESPUESTAS[String(v)] ?? String(v ?? ""),
        "Puntuación total": "",
        "Resultado": "",
      });
    });

    rows.push({
      "Nombre del cuestionario": "",
      "Estado": "",
      "Preguntas": "",
      "Respuestas": "",
      "Puntuación total": puntuacionTotal,
      "Resultado":
        puntuacionTotal >= 3
          ? "Presenta riesgo de disfagia"
          : "No presenta riesgo de disfagia",
    });
  }

  // =================================
  // CRIBADO NUTRICIONAL (FORMATO ESPECIAL)
  // =================================
  else if (instrumentoId === "CRIBADO-NUTRICIONAL") {

const tipoPacienteRaw = q.items?.__selector ?? "";
const tipoPacienteTexto = TIPO_PACIENTE_LABEL[tipoPacienteRaw] ?? tipoPacienteRaw;

    const tipoCribado = q.items?.__tipoPaciente;

    const respuestasCribado =
      tipoCribado === "MNA"  ? q.items?.mna  :
      tipoCribado === "MST"  ? q.items?.mst  :
      tipoCribado === "NRS"  ? q.items?.nrs  :
      tipoCribado === "MUST" ? q.items?.must :
      {};

let puntuacionTotal = 0;

Object.entries(respuestasCribado || {}).forEach(([key, value]) => {
  // En MNA, NO sumar subpreguntas K1, K2, K3
  if (tipoCribado === "MNA" && ["K1", "K2", "K3"].includes(key)) {
    return;
  }

  const n = Number(value);
  if (!isNaN(n)) {
    puntuacionTotal += n;
  }
});

rows.push({
  "Nombre del cuestionario": LABEL_CUESTIONARIOS[instrumentoId],
  "Estado": estadoTexto,
  "Tipo de paciente": tipoPacienteTexto,
  "Preguntas": "",
  "Respuestas": "",
  "Puntuación total": "",
  "Clasificación": "",
});

const entriesOrdenadas =
  tipoCribado === "MNA"
    ? Object.entries(respuestasCribado || {}).sort(([a], [b]) =>
        a.localeCompare(b, "es", { numeric: true })
      )
    : tipoCribado === "MST"
      ? ["q1", "q1b", "q2"]
          .filter((k) => k in (respuestasCribado || {}))
          .map((k) => [k, respuestasCribado[k]])
    : tipoCribado === "NRS"
      ? ["n1", "n2", "n3", "n4"]
          .filter((k) => k in (respuestasCribado || {}))
          .map((k) => [k, respuestasCribado[k]])
    : tipoCribado === "MUST"
      ? ["step1", "step2", "step3"]
          .filter((k) => k in (respuestasCribado || {}))
          .map((k) => [k, respuestasCribado[k]])
      : Object.entries(respuestasCribado || {});

entriesOrdenadas.forEach(([key, value]) => {

  // En MNA, NO mostrar la pregunta K agregada
  if (tipoCribado === "MNA" && key === "K") return;

  rows.push({
    "Nombre del cuestionario": "",
    "Estado": "",
    "Tipo de paciente": "",
"Preguntas":
  tipoCribado === "MNA"
    ? (MNA_PREGUNTAS[key] || key)
    : tipoCribado === "MST"
      ? (MST_PREGUNTAS[key] || key)
      : tipoCribado === "NRS"
        ? (NRS_PREGUNTAS[key] || key)
        : tipoCribado === "MUST"
          ? (MUST_PREGUNTAS[key] || key)
          : key,
"Respuestas":
  tipoCribado === "MNA"
    ? (MNA_RESPUESTAS[key]?.[String(value)] ?? String(value ?? ""))
    : tipoCribado === "MST"
      ? (MST_RESPUESTAS[key]?.[String(value)] ?? String(value ?? ""))
      : tipoCribado === "NRS"
        ? (NRS_RESPUESTAS[key]?.[String(value)] ?? String(value ?? ""))
        : tipoCribado === "MUST"
          ? (MUST_RESPUESTAS[key]?.[String(value)] ?? String(value ?? ""))
          : String(value ?? ""),
    "Puntuación total": "",
    "Clasificación": "",
  });
});

rows.push({
  "Nombre del cuestionario": "",
  "Estado": "",
  "Tipo de paciente": "",
  "Preguntas": "",
  "Respuestas": "",
  "Puntuación total": puntuacionTotal,
  "Clasificación": clasificarCribadoNutricional(
    tipoCribado,
    puntuacionTotal
  ),
});
  }

// =================================
// VALORACIÓN NUTRICIONAL (GLIM)
// =================================

else if (instrumentoId === "CRITERIOS-GLIM") {

  const GLIM_ORDEN = ["p1", "p2", "p3", "p4", "p5"];

  const resultadoGLIM = q.resultado;

  // Cabecera
  rows.push({
    "Nombre del cuestionario": LABEL_CUESTIONARIOS[instrumentoId],
    "Estado": estadoTexto,
    "Preguntas": "",
    "Respuestas": "",
    "Resultado": "",
  });

  // Preguntas fenotípicas y etiológicas
GLIM_ORDEN.forEach((key) => {
  const value = q.items?.glim?.[key];
  if (value == null) return;

  rows.push({
    "Nombre del cuestionario": "",
    "Estado": "",
    "Preguntas": GLIM_PREGUNTAS[key] || key,
    "Respuestas": String(value),
    "Resultado": "",
  });
});

  // Fila final con resultado
  rows.push({
    "Nombre del cuestionario": "",
    "Estado": "",
    "Preguntas": "",
    "Respuestas": "",
    "Resultado": resultadoGLIM,
  });
}

// =========================
// CAVIDAD ORAL (FORMATO ESPECIAL)
// =========================
else if (instrumentoId === "CAVIDAD-ORAL") {

  const respuestas = q.items?.cavidadoral || {};

  rows.push({
    "Nombre del cuestionario": LABEL_CUESTIONARIOS[instrumentoId],
    "Estado": estadoTexto,
    "Preguntas": "",
    "Respuestas": "",
  });

["q1", "q2", "q3", "q4", "q5", "q6", "q7"]
  .filter((k) => k in respuestas)
  .forEach((key) => {
    rows.push({
      "Nombre del cuestionario": "",
      "Estado": "",
      "Preguntas": CAVIDAD_ORAL_PREGUNTAS[key] || key,
      "Respuestas": String(respuestas[key] ?? ""),
    });
  });
}

// =========================
// MECV-V (FORMATO ESPECIAL)
// =========================
else if (instrumentoId === "MECV-V") {

const mecvv = q.items?.mecvv || {};
const resultado = q.resultado || "";

// 1️⃣ Extraer signos marcados
const seguridad = [];
const eficacia = [];

Object.keys(mecvv).forEach((k) => {
  if (!mecvv[k]) return;

  const [, tipo, signo, viscosidad, volumen] =
    k.match(/(seg|efi)_(.+?)_(nectar|liquido|pudin)_(\d+)/) || [];

  if (!tipo) return;

  const texto = `${signo} ${viscosidad} ${volumen}`;

  if (tipo === "seg") seguridad.push(texto);
  if (tipo === "efi") eficacia.push(texto);
});

// 2️⃣ Recomendaciones dietéticas en ARRAY
const recomendacionesArr = [];

if (mecvv.viscosidad) {
  recomendacionesArr.push(`Viscosidad: ${mecvv.viscosidad}`);
}
if (mecvv.volumen) {
  recomendacionesArr.push(`Volumen: ${mecvv.volumen}`);
}

// 3️⃣ Fila cabecera
rows.push({
  "Nombre del cuestionario": "MECV-V",
  "Estado": estadoTexto,
  "Alteraciones o signos de seguridad": "",
  "Alteraciones o signos de eficacia": "",
  "Resultado": "",
  "Recomendaciones dietéticas": "",
});

// 4️⃣ Seguridad → una fila por signo
seguridad.forEach((s) => {
  rows.push({
    "Nombre del cuestionario": "",
    "Estado": "",
    "Alteraciones o signos de seguridad": s,
    "Alteraciones o signos de eficacia": "",
    "Resultado": "",
    "Recomendaciones dietéticas": "",
  });
});

// 5️⃣ Eficacia → una fila por signo
eficacia.forEach((e) => {
  rows.push({
    "Nombre del cuestionario": "",
    "Estado": "",
    "Alteraciones o signos de seguridad": "",
    "Alteraciones o signos de eficacia": e,
    "Resultado": "",
    "Recomendaciones dietéticas": "",
  });
});

// 6️⃣ Resultado → su propia fila
rows.push({
  "Nombre del cuestionario": "",
  "Estado": "",
  "Alteraciones o signos de seguridad": "",
  "Alteraciones o signos de eficacia": "",
  "Resultado": resultado,
  "Recomendaciones dietéticas": "",
});

// 7️⃣ Recomendaciones → una fila por elemento
recomendacionesArr.forEach((r) => {
  rows.push({
    "Nombre del cuestionario": "",
    "Estado": "",
    "Alteraciones o signos de seguridad": "",
    "Alteraciones o signos de eficacia": "",
    "Resultado": "",
    "Recomendaciones dietéticas": r,
  });
});
}

  // =========================
  // RESTO (FORMATO ORIGINAL)
  // =========================
  else {
    rows.push({
      "Nombre del cuestionario": LABEL_CUESTIONARIOS[instrumentoId],
      "Estado": estadoTexto,
      "Campo": "",
      "Valor": "",
    });

    Object.entries(q.items || {}).forEach(([campo, valor]) => {
      rows.push({
        "Nombre del cuestionario": "",
        "Estado": "",
        "Campo": campo,
        "Valor":
          typeof valor === "object"
            ? JSON.stringify(valor)
            : String(valor ?? ""),
      });
    });
  }

  const ws = XLSX.utils.json_to_sheet(rows);

  // Ancho de columnas
  if (instrumentoId === "EAT-10") {
    ws["!cols"] = [
      { wch: 35 },
      { wch: 14 },
      { wch: 80 },
      { wch: 25 },
      { wch: 25 },
      { wch: 30 },
    ];
  } else if (instrumentoId === "CRIBADO-NUTRICIONAL") {
    ws["!cols"] = [
      { wch: 30 },
      { wch: 14 },
      { wch: 30 },
      { wch: 90 },
      { wch: 38 },
      { wch: 25 },
      { wch: 35 },
    ];
} else if (instrumentoId === "CRITERIOS-GLIM") {
  ws["!cols"] = [
    { wch: 30 },
    { wch: 14 },
    { wch: 70 },
    { wch: 70 },
    { wch: 30 },
  ]; 
} else if (instrumentoId === "CAVIDAD-ORAL") {
  ws["!cols"] = [
    { wch: 30 },
    { wch: 14 },
    { wch: 40 },
    { wch: 40 },
  ];
} else if (instrumentoId === "MECV-V") {
  ws["!cols"] = [
    { wch: 30 }, 
    { wch: 14 }, 
    { wch: 45 }, 
    { wch: 45 }, 
    { wch: 60 }, 
    { wch: 40 }, 
  ];
} else {
    ws["!cols"] = [
      { wch: 30 },
      { wch: 15 },
      { wch: 40 },
      { wch: 40 },
    ];
  }

  XLSX.utils.book_append_sheet(wb, ws, nombreHoja);

  // Estilos
  if (ws["!ref"]) {
    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const addr = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = ws[addr];
        if (!cell) continue;

        cell.s = {
          alignment: {
            horizontal: "center",
            vertical: "center",
            wrapText: true,
          },
          font: {
            sz: 12,
            bold: R === 0,
          },
        };
      }
    }
  }
}

// ---- PAUTAS ----
if (pautas) {

  const ORDEN_PAUTAS = [
    "valdisfagiacondiag",
    "valdisfagiaconsos",
    "recomendaciones",
    "recetas",
    "ejercicios",
    "pautasejercicioslogrehab",
    "farmacos",
  ];

  const LABEL_PAUTAS = {
    valdisfagiacondiag:
      "Valoración disfagia: Usuario/a con diagnóstico de disfagia orofaríngea",
    valdisfagiaconsos:
      "Valoración disfagia: Usuario/a con sospecha de disfagia orofaríngea",
    recomendaciones: "Recomendaciones",
    recetas: "Recetas",
    ejercicios: "Actividad / Ejercicio físico",
    pautasejercicioslogrehab:
      "Pautas / Ejercicios de logopedia y rehabilitación",
    farmacos: "Disfagia y fármacos",
  };

  const aoaPautas = [
    [
      "Nombre de la sección a la que pertenece la recomendación o pauta",
      "Recomendaciones y pautas seleccionadas para el paciente",
    ],
    ...ORDEN_PAUTAS.flatMap((k) => {
      if (pautas[k] == null) return [];

      const label = LABEL_PAUTAS[k] || k;
      const value = pautas[k];

      if (Array.isArray(value)) {
        return value.map((item) => [
          label,
          typeof item === "object"
            ? JSON.stringify(item)
            : String(item ?? ""),
        ]);
      }

      if (typeof value === "object") {
        return Object.entries(value).map(([_, v]) => [
          label,
          typeof v === "object"
            ? JSON.stringify(v)
            : String(v ?? ""),
        ]);
      }

      return [[label, String(value)]];
    }),
  ];

  const wsP = XLSX.utils.aoa_to_sheet(aoaPautas);

  wsP["!cols"] = [
    { wch: 80 },
    { wch: 100 },
  ];

  // Estilo: centrado, tamaño 12 y encabezados en negrita
  if (wsP["!ref"]) {
    const range = XLSX.utils.decode_range(wsP["!ref"]);
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const addr = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = wsP[addr];
        if (!cell) continue;

        cell.s = {
          alignment: {
            horizontal: "center",
            vertical: "center",
            wrapText: true,
          },
          font: {
            sz: 12,
            bold: R === 0,
          },
        };
      }
    }
  }

  XLSX.utils.book_append_sheet(
    wb,
    wsP,
    `Visita ${numeroVisita || ""} - Pautas`
  );
}

// ======================
// DESCARGA
// ======================
const fechaArchivo = fechaFormateada || "sin-fecha";

XLSX.writeFile(
  wb,
  `paciente_${pacienteId}_visita${numeroVisita || ""}_${fechaArchivo}.xlsx`,
  {
    bookType: "xlsx",
    cellStyles: true,
  }
);
  } catch (e) {
    console.error(e);
    alert("No se pudo generar el Excel de la visita.");
  }
};

  const guardar = async () => {
    if (!visita || !puedeEditarVisita) return;
    setGuardando(true);
    try {
      const { id, ...rest } = visita;

      const peso = rest?.medidas?.peso ?? rest?.peso ?? null;
      const talla = rest?.medidas?.talla ?? rest?.talla ?? null;
      const imc = peso != null && talla != null ? computeIMC(peso, talla) : null;

      const payload = {
        ...rest,
        creadoPor: rest.creadoPor || user.uid,
        creadoPorEmail: rest.creadoPorEmail || user.email || null,
        updatedAt: serverTimestamp(),
        peso: peso ?? null,
        talla: talla ?? null,
        imc: imc ?? null,
        medidas: { ...(rest.medidas || {}), peso: peso ?? null, talla: talla ?? null, imc: imc ?? null },
      };

      await setDoc(visitaRef, payload, { merge: true });
      alert("Visita guardada ✅");
    } catch (e) {
      console.error("setDoc visita error:", e.code, e.message);
      alert(`No se pudo guardar la visita (${e.code || "error"}).`);
    } finally {
      setGuardando(false);
    }
  };

  const eliminarVisita = async () => {
    if (!puedeEditarVisita) {
      alert("No tienes permiso para eliminar esta visita.");
      return;
    }
    const ok = window.confirm("¿Eliminar esta visita y TODOS sus cuestionarios? Esta acción no se puede deshacer.");
    if (!ok) return;

    try {
      setEliminando(true);
      const respuestasSnap = await getDocs(respRef);
      const deletes = respuestasSnap.docs.map((d) => deleteDoc(d.ref));
      await Promise.all(deletes);
      await deleteDoc(visitaRef);
      navigate(`/pacientes/${pacienteId}`);
    } catch (e) {
      console.error("eliminar visita error:", e.code || e.message);
      alert(`No se pudo eliminar la visita (${e.code || "error"}).`);
    } finally {
      setEliminando(false);
    }
  };

  const crearCuestionario = async (instrumentoId) => {
    try {
      if (!user) {
        alert("Inicia sesión para crear cuestionarios.");
        return;
      }
      if (!puedeEditarVisita) {
        alert("No tienes permiso para crear cuestionarios en esta visita.");
        return;
      }

      const yaExisteEnMemoria = cuestionarios.some((q) => q.id === instrumentoId || q.instrumentoId === instrumentoId);
      if (yaExisteEnMemoria) {
        alert(`Ya existe un ${instrumentoId} en esta visita.`);
        return;
      }

      const qRef = doc(respRef, instrumentoId);
      const qSnap = await getDocOnce(qRef);
      if (qSnap.exists()) {
        alert(`Ya existe un ${instrumentoId} en esta visita.`);
        return;
      }

      await setDoc(qRef, {
        instrumentoId,
        version: 1,
        completado: false,
        items: {},
        createdAt: serverTimestamp(),
        creadoPor: user.uid,
        creadoPorEmail: user.email || null,
      });
    } catch (e) {
      console.error("create cuestionario error:", e.code, e.message);
      alert(`No se pudo crear el cuestionario (${e.code || "error"}).`);
    }
  };

  const eliminarCuestionario = async (instrumentoId) => {
    if (!puedeEditarVisita) {
      alert("No tienes permiso para eliminar cuestionarios en esta visita.");
      return;
    }
    const ok = window.confirm(`¿Eliminar el cuestionario ${instrumentoId}?`);
    if (!ok) return;
    try {
      await deleteDoc(doc(respRef, instrumentoId));
    } catch (e) {
      console.error("delete cuestionario error:", e.code, e.message);
      alert(`No se pudo eliminar (${e.code || "error"}).`);
    }
  };

  if (loading || !visita)
    return (
      <div className="p-4 max-w-xl mx-auto">
        <p>{loading ? "Cargando visita..." : "Cargando visita..."}</p>
      </div>
    );

  const existe = (instrumentoId) =>
    cuestionarios.some((q) => q.id === instrumentoId || q.instrumentoId === instrumentoId);

  return (
    <div className="p-4 max-w-xl mx-auto flex flex-col gap-4">
      {/* Encabezado */}
      <div className="flex items-center justify-between">
        <Button onClick={() => navigate(`/pacientes/${pacienteId}`)} className="flex items-center gap-1 px-3 py-1.5" >
          <span>Volver al paciente</span>
        </Button>

        <h4 className="font-bold text-right">
          {numeroVisita ? `Visita ${numeroVisita} · ${fechaFormateada}` : `Visita · ${fechaFormateada}`}
        </h4>
      </div>

      {/* Datos de la visita */}
      <div className="bg-[#dfeaf7] p-4 rounded-lg shadow flex flex-col gap-3">
        <div className="grid grid-cols-1 gap-3">
          <div>
            <label className="text-sm font-semibold">Fecha</label>
            <input type="date" className="w-full border p-2 rounded bg-white" value={visita.fecha || ""} onChange={(e) => setVisita((v) => ({ ...v, fecha: e.target.value }))} disabled={!puedeEditarVisita} />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-sm font-semibold">Peso (kg)</label>
            <input type="number" step="0.1" className="w-full border p-2 rounded" value={visita.medidas?.peso ?? ""} onChange={(e) => onChangeMedida("peso", e.target.value)} disabled={!puedeEditarVisita} />
          </div>
          <div>
            <label className="text-sm font-semibold">Talla (cm)</label>
            <input type="number" step="0.1" className="w-full border p-2 rounded" value={visita.medidas?.talla ?? ""} onChange={(e) => onChangeMedida("talla", e.target.value)} disabled={!puedeEditarVisita} />
          </div>
          <div>
            <label className="text-sm font-semibold">IMC</label>
            <input readOnly className="w-full border p-2 rounded bg-gray-100" value={visita.medidas?.imc ?? ""} />
          </div>
        </div>

        {/* Estado + acciones */}
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-1">
            <label className="text-sm font-semibold">Estado</label>
            <select className="w-full border p-2 rounded bg-white" value={visita.completada ? "Completo" : "Pendiente"} onChange={(e) => setVisita((prev) => ({ ...(prev || {}), completada: e.target.value === "Completo", estado: e.target.value, })) } disabled={!puedeEditarVisita} >
              <option value="Pendiente">Pendiente</option>
              <option value="Completo">Completo</option>
            </select>
          </div>

          <div className="col-span-2 flex items-end justify-end gap-2">
            <Button onClick={guardar} disabled={!puedeEditarVisita || guardando || eliminando}>
              {guardando ? "Guardando..." : "Guardar"}
            </Button>

            <Button className="bg-red-600 hover:bg-red-700 text-white flex items-center" onClick={eliminarVisita} disabled={!puedeEditarVisita || eliminando || guardando} title="Eliminar visita" >
              {eliminando ? "Eliminando..." : "Eliminar"}
            </Button>
          </div>
        </div>
      </div>

      {/* Botón Informe (Excel) debajo del cuadro azul */}
      <div className="flex justify-center">
        <Button onClick={descargarExcel} className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white" title="Descargar Informe (Excel)" >
          Informe (Excel)
        </Button>
      </div>

      {/* ====================== BLOQUE 1: CUESTIONARIOS ====================== */}
      <div className="bg-white p-4 rounded-lg border flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h5 className="font-semibold">Cuestionarios</h5>
        </div>

        {/* Grid fijo: 2 col | fila 2 ocupa todo | fila 3 dos col */}
        <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(2, 1fr)", gridTemplateAreas: ` "eat10 cribado" "glim  glim" "oral  mecv" `, }} >
          <div style={{ gridArea: "eat10" }}>
            <Button onClick={() => crearCuestionario("EAT-10")} disabled={existe("EAT-10") || !puedeEditarVisita} className={`w-full justify-center py-2 ${ existe("EAT-10") || !puedeEditarVisita ? "bg-gray-200 text-gray-600" : "" }`} title="Cribado disfagia (EAT-10)" >
              Cribado disfagia (EAT-10)
            </Button>
          </div>

          <div style={{ gridArea: "cribado" }}>
            <Button onClick={() => crearCuestionario("CRIBADO-NUTRICIONAL")} disabled={existe("CRIBADO-NUTRICIONAL") || !puedeEditarVisita} className={`w-full justify-center py-2 ${ existe("CRIBADO-NUTRICIONAL") || !puedeEditarVisita ? "bg-gray-200 text-gray-600" : "" }`} title="Cribado nutricional">
              Cribado nutricional
            </Button>
          </div>

          <div style={{ gridArea: "glim" }}>
            <Button onClick={() => crearCuestionario("CRITERIOS-GLIM")} disabled={existe("CRITERIOS-GLIM") || !puedeEditarVisita} className={`w-full justify-center py-2 ${ existe("CRITERIOS-GLIM") || !puedeEditarVisita ? "bg-gray-200 text-gray-600" : "" }`} title="Valoración nutricional">
              Valoración nutricional
            </Button>
          </div>

          <div style={{ gridArea: "oral" }}>
            <Button onClick={() => crearCuestionario("CAVIDAD-ORAL")} disabled={existe("CAVIDAD-ORAL") || !puedeEditarVisita} className={`w-full justify-center py-2 ${ existe("CAVIDAD-ORAL") || !puedeEditarVisita ? "bg-gray-200 text-gray-600" : "" }`} title="Valoración cavidad oral">
              Valoración cavidad oral
            </Button>
          </div>

          <div style={{ gridArea: "mecv" }}>
            <Button onClick={() => crearCuestionario("MECV-V")} disabled={existe("MECV-V") || !puedeEditarVisita} className={`w-full justify-center py-2 ${ existe("MECV-V") || !puedeEditarVisita ? "bg-gray-200 text-gray-600" : "" }`} title="MECV-V">
              MECV-V
            </Button>
          </div>
        </div>

        {cuestionarios.length === 0 ? (
          <p className="text-gray-600">Aún no hay cuestionarios.</p>
        ) : (
          <ul className="flex flex-col gap-2 mt-2">
            {sortedCuestionarios.map((c) => (
              <li key={c.id} className="p-3 rounded border bg-[#f7fbff] flex items-center justify-between">
                <div>
                  <div className="font-medium">{nombreCuestionario(c)}</div>
                  <div className="text-sm mt-0.5">
                    <span className={c.completado ? "text-green-700" : "text-amber-700"}>
                      {c.completado ? "Completo" : "Pendiente"}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button onClick={() => navigate( `/pacientes/${pacienteId}/visitas/${vid}/cuestionarios/${c.instrumentoId}` )}>
                    Abrir
                  </Button>
                  <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => eliminarCuestionario(c.id || c.instrumentoId)} disabled={!puedeEditarVisita}>
                    Eliminar
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ============== BLOQUE 2: RECOMENDACIONES Y PAUTAS ============== */}
      <div className="bg-white p-4 rounded-lg border flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h5 className="font-semibold">Recomendaciones y pautas</h5>
        </div>

<div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
  {RECURSOS_DEF.map((r) => (
    <div key={r.path} className={ r.label === "Pautas / Ejercicios de logopedia y rehabilitación" ? "sm:col-span-2" : "" }>
      <Button onClick={() => navigate(r.path)} className="w-full">
        {r.label}
      </Button>
    </div>
  ))}
</div>

        <p className="text-center text-gray-500">
          Estas opciones no generan un cuestionario en la visita; son accesos directos a otras pantallas.
        </p>
        
<Button onClick={() => navigate(`/pacientes/${pacienteId}/visitas/${vid}/pautas`)} className="bg-disfagiapp-blue text-white">
  Seleccionar recomendaciones y pautas para generar PDF
</Button>

      </div>
    </div>
  );
}